import { Model, Attr, BelongsTo, HasOne, HasMany } from 'spraypaint';
import ApplicationRecord from './ApplicationRecord';
import Auction from './Auction';
import Media from './Media';

const IMAGE_SIZES = [
  'large',
  'medium',
  'small',
  'thumbnail',
  'x_small',
  'xx_small',
] as const;

type ImageSizes = typeof IMAGE_SIZES[number];

type ImageUrl = {
  media_type: string;
  original: string;
} & Record<ImageSizes, ImageUrlItem>;

interface ImageUrlItem {
  url: string;
  width: number;
}

@Model()
export default class Lot extends ApplicationRecord {
  static jsonapiType = 'lots';

  static listHeaders() {
    return [
      {
        text: 'Title',
        value: 'title',
      },
      {
        text: 'Image',
        value: 'imageUrls',
        width: 70,
        sortable: false,
      },
      {
        text: 'Number',
        value: 'number',
        width: 120,
      },
      {
        text: 'Preview',
        value: 'numberPreview',
        width: 120,
      },
      {
        text: 'Bids',
        value: 'bidCount',
        width: 80,
      },
    ];
  }

  static scopeFactory() {
    return Lot.includes([]);
  }

  static defaultOptions() {
    return {
      sortBy: ['ordering'],
      sortDesc: [false],
    };
  }

  @Attr() created: string;
  @Attr() title: string;
  @Attr() number: string | null;
  @Attr() numberPreview: string;
  @Attr() description: string;
  @Attr() salePrice: number;
  @Attr() bidCount: number;
  @Attr() imageUrls: ImageUrl[];

  @HasOne() auction: Auction;
  @HasMany() images: Media[];

  getFeaturedImageSrc(size: ImageSizes | 'original' = 'thumbnail') {
    if (this.images.length === 0) {
      return;
    }

    if (!this.imageUrls[0][size]) {
      for (const s of IMAGE_SIZES) {
        if (this.imageUrls[0][s].url) {
          return this.imageUrls[0][s].url;
        }
      }
      return;
    }

    if (size === 'original') {
      return this.imageUrls[0].original;
    }

    return this.imageUrls[0][size].url || this.imageUrls[0].thumbnail.url;
  }

  getFeaturedImageSrcSet() {
    if (this.images.length === 0) {
      return;
    }

    const images = this.imageUrls[0];

    if (!(images.media_type === 'image')) {
      return;
    }

    const srcs = IMAGE_SIZES.map(size => {
      if (!images[size]) {
        return '';
      }
      return `${images[size].url} ${images[size].width}w`;
    });

    return srcs.join(', ');
  }
}
